import { api } from 'aws-blocks';
import Modal from '../../Components/Modal';
import ProfileAvatar from '../../Components/ProfileAvatar';
import BkButton from '../../Components/ui/BkButton';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSnackBarContext } from '../../contexts/SnackBarContext';
import useApiAction from '../../hooks/use-api-action';
import {
  canUseAvatar,
  PROFILE_AVATARS,
} from '../../profile-avatars/registry';

export type ProfileAvatarPickerProps = {
  show: boolean;
  onClose: () => void;
};

export default function ProfileAvatarPicker({
  show,
  onClose,
}: ProfileAvatarPickerProps) {
  const { userData } = useAuthContext();
  const { addSnackBar } = useSnackBarContext();
  const ownedColorIds = userData?.ownedColorIds;
  const ownedIconIds = userData?.ownedIconIds;
  const current = userData?.avatarId;

  const [updateAvatar, loading] = useApiAction({
    action: async (payload: { avatarId?: string }) =>
      api.updateAvatar(String(payload.avatarId)),
    reloadUserData: true,
  });

  async function handleSelect(avatarId: string) {
    if (!canUseAvatar(avatarId, ownedColorIds, ownedIconIds)) {
      addSnackBar({
        title: 'Locked',
        message: 'Unlock this portrait via skins or the Battle Pass.',
        variant: 'warning',
        timeout: 2500,
      });
      return;
    }
    try {
      await updateAvatar({ avatarId });
      addSnackBar({
        title: 'Portrait updated',
        message: 'Your profile image has been saved.',
        variant: 'success',
        timeout: 2000,
      });
      onClose();
    } catch (err) {
      addSnackBar({
        title: 'ERROR',
        message: err instanceof Error ? err.message : String(err),
        variant: 'danger',
        timeout: 2500,
      });
    }
  }

  return (
    <Modal show={show} onClose={onClose} title="Choose portrait">
      <div className="grid max-h-[60vh] grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-3">
        {PROFILE_AVATARS.map((avatar) => {
          const usable = canUseAvatar(avatar.id, ownedColorIds, ownedIconIds);
          const selected = current === avatar.id;
          return (
            <button
              key={avatar.id}
              type="button"
              disabled={loading || !usable}
              onClick={() => void handleSelect(avatar.id)}
              className={[
                'flex flex-col items-center gap-2 border p-3 transition-colors',
                selected
                  ? 'border-bk-gold bg-bk-gold/15'
                  : usable
                    ? 'border-bk-border hover:border-bk-gold'
                    : 'cursor-not-allowed border-bk-border opacity-45',
              ].join(' ')}
            >
              <ProfileAvatar avatarId={avatar.id} size="md" />
              <span className="text-center text-[11px] font-bold uppercase tracking-wide text-bk-text">
                {avatar.label}
              </span>
              {!usable ? (
                <span className="text-[10px] text-bk-muted">Locked</span>
              ) : selected ? (
                <span className="text-[10px] text-bk-gold">Equipped</span>
              ) : null}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex justify-end">
        <BkButton variant="ghost" size="sm" onClick={onClose}>
          Close
        </BkButton>
      </div>
    </Modal>
  );
}
